import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SortEvent } from 'src/app/shared/advanced-table/sortable.directive';
import { Column } from 'src/app/shared/advanced-table/advanced-table.component';
import { DiaInhabil } from './dias-inhabiles.model';
import { diasInhabilesTranslations } from './translations-dias-inhabiles';
import { formatDateString, getCurrentLanguage } from '../core/helpers/utils';
import { CasosService } from '../casos/casos.service';
import { AuthenticationService } from '../core/service/auth.service';
import { DiasInhabilesService } from './dias-inhabiles.service';

@Component({
  selector: 'app-dias-inhabiles',
  templateUrl: './dias-inhabiles.component.html',
  styleUrls: ['./dias-inhabiles.component.scss']
})
export class DiasInhabilesComponent implements OnInit {

  records: DiaInhabil[] = [];
  columns: Column[] = [];
  pageSizeOptions: number[] = [10, 25, 50, 100];
  buttonTitle: string = '';
  buttonLink: string = '';
  loaded: boolean = false;
  error: string = '';
  allDatasearch: DiaInhabil[] = [];
  id_caso!: string | null;
  estadoExpediente: string | undefined;
  disableOptions: boolean = false;
  disableAnadirDiaInhabil: boolean = true;
  useDateRange: boolean = false;

  nuevoDiaInhabil: DiaInhabil = {
    id_expediente: '',
    descripcion: '',
    fecha_inicio: '',
    fecha_fin: '',
    tipo: 'DIA_INHABIL'
  };

  showConfirmDeleteModal: boolean = false;
  diaInhabilToDelete: DiaInhabil | null = null;

  constructor(
    private diasInhabilesService: DiasInhabilesService,
    private route: ActivatedRoute,
    private casosService: CasosService,
    private authService: AuthenticationService
  ) { }

  ngOnInit(): void {
    this.id_caso = this.route.snapshot.paramMap.get('id');
    if (this.id_caso) {
      this.nuevoDiaInhabil.id_expediente = this.id_caso;

      this.casosService.getCaso(this.id_caso).subscribe({
        next: (caso) => {
          this.estadoExpediente = caso.estado;
          const estadosCerrados = ['CERRADO_LAUDO', 'CERRADO_ACUERDO', 'CERRADO_OTROS', 'CERRADO_REENVIO'];
          this.disableOptions = estadosCerrados.includes(this.estadoExpediente ?? '');

          this.authService.currentUser().subscribe(user => {
            if (user?.nivel_acceso === 'ADMIN') {
              this.disableAnadirDiaInhabil = false;
            }
            this.loaded = true;
          });
        },
        error: (error) => {
          console.error('Error obteniendo el estado del expediente:', error);
          this.error = error;
        }
      });

      this._fetchData();
    }
  }

  isCaseClosed(): boolean {
    const estadosCerrados = ['CERRADO_LAUDO', 'CERRADO_ACUERDO', 'CERRADO_OTROS', 'CERRADO_REENVIO'];
    return estadosCerrados.includes(this.estadoExpediente ?? '');
  }

  onDateRangeChange(): void {
    this.nuevoDiaInhabil.fecha_inicio = '';
    this.nuevoDiaInhabil.fecha_fin = '';
  }

  setDateRange(useRange: boolean): void {
    this.useDateRange = useRange;
    this.onDateRangeChange();
  }

  _fetchData(): void {
    if (this.id_caso) {
      this.diasInhabilesService.getDiasInhabilesExpediente(this.id_caso).subscribe(
        (resDiasInhabiles) => {
          this.records = resDiasInhabiles.sort((a, b) => (a.fecha_fin ?? '').localeCompare(b.fecha_fin ?? ''));
          this.allDatasearch = [...this.records];
          this.initTableCofig();
          this.loaded = true;
        },
        (error: string) => {
          this.error = error;
          this.loaded = false;
        }
      );
    }
  }

  onSubmit(f: any): void {
    if (f.valid && this.id_caso) {
      const diaInhabilParaEnviar: DiaInhabil = {
        ...this.nuevoDiaInhabil,
        tipo: 'DIA_INHABIL'
      };

      if (!this.useDateRange) {
        delete diaInhabilParaEnviar.fecha_inicio;
      }

      this.diasInhabilesService.newDiaInhabil(diaInhabilParaEnviar).subscribe(
        () => {
          f.resetForm();
          this.useDateRange = false;
          this.nuevoDiaInhabil = {
            id_expediente: this.id_caso ?? '',
            descripcion: '',
            fecha_inicio: '',
            fecha_fin: '',
            tipo: 'DIA_INHABIL'
          };
          this._fetchData();
        },
        () => {
          console.error('Error al añadir el día inhábil:', diaInhabilParaEnviar);
        }
      );
    }
  }

  initTableCofig(): void {
    const currentLanguage: string = getCurrentLanguage();
    const currentTranslations = diasInhabilesTranslations[currentLanguage].TABLE;
    this.columns = [
      {
        name: 'descripcion',
        label: currentTranslations.DESCRIPCION,
        formatter: (record: DiaInhabil) => record.descripcion,
        width: 445,
      },
      {
        name: 'fecha_fin',
        label: currentTranslations.FECHA,
        formatter: (record: DiaInhabil) => this.formatFechaRango(record),
        width: 50,
      },
      {
        name: 'accionesDiasInhabiles',
        label: currentTranslations.ACTIONS,
        formatter: (record: DiaInhabil) => null,
        width: 50,
        sort: false
      }
    ];
  }

  formatFechaRango(record: DiaInhabil): string {
    const currentLanguage: string = getCurrentLanguage();
    const inicio = record.fecha_inicio ? formatDateString(record.fecha_inicio, currentLanguage) : '';
    const fin = record.fecha_fin ? formatDateString(record.fecha_fin, currentLanguage) : '';
    if (inicio && fin && inicio !== fin) {
      return `${inicio} - ${fin}`;
    }
    return fin || inicio || '';
  }

  onSort(event: SortEvent): void {
    if (event.direction === '') {
      this.records = this.allDatasearch;
    } else {
      this.records = [...this.records].sort((a, b) => {
        const valueA = (a[event.column] ?? '') as string | number;
        const valueB = (b[event.column] ?? '') as string | number;
        const res = this.compare(valueA, valueB);
        return event.direction === 'asc' ? res : -res;
      });
    }
  }

  compare(v1: string | number, v2: string | number): any {
    return v1 < v2 ? -1 : v1 > v2 ? 1 : 0;
  }

  normalizeText(text: string): string {
    return text.trim().toLocaleLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  }

  matches(row: DiaInhabil, term: string): boolean {
    const fecha = this.formatFechaRango(row);
    return this.normalizeText((row.descripcion) as string).includes(this.normalizeText(term))
      || this.normalizeText(fecha).includes(this.normalizeText(term));
  }

  searchData(searchTerm: string): void {
    if (searchTerm === '') {
      this.records = this.allDatasearch;
    } else {
      this.records = this.allDatasearch.filter(record => this.matches(record, searchTerm));
    }
  }

  openDeleteModal(diaInhabil: DiaInhabil) {
    this.diaInhabilToDelete = diaInhabil;
    this.showConfirmDeleteModal = true;
  }

  closeDeleteModal() {
    this.showConfirmDeleteModal = false;
    this.diaInhabilToDelete = null;
  }

  confirmDelete() {
    if (this.diaInhabilToDelete?.id) {
      this.diasInhabilesService.deleteDiaInhabil(this.diaInhabilToDelete.id).subscribe(() => {
        this._fetchData();
        this.closeDeleteModal();
      });
    }
  }
}
